/**
 * MeineBetriebsvereinbarungenPage.jsx
 * Mitarbeiter-Seite: eigene BVen sehen, unterschreiben, PDF herunterladen
 * Erreichbar: Profil → Betriebsvereinbarungen
 */
import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, CheckCircle2, Clock, Loader2, Download,
  ChevronDown, ChevronUp, PenLine, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SignaturePad from '../components/ui/SignaturePad';
import { MarkdownContent } from '../components/MarkdownField';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function StatusBadge({ status }) {
  if (status === 'pending') return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
      <Clock className="h-3 w-3" /> Ausstehend
    </span>
  );
  if (status === 'employee_signed') return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
      <CheckCircle2 className="h-3 w-3" /> Unterschrieben
    </span>
  );
  if (status === 'completed') return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
      <CheckCircle2 className="h-3 w-3" /> Vollständig
    </span>
  );
  return null;
}

function BVSignCard({ assignment, onSigned }) {
  const [expanded, setExpanded] = useState(assignment.status === 'pending');
  const [confirmed, setConfirmed] = useState(false);
  const [sigError, setSigError] = useState(false);
  const [signing, setSigning] = useState(false);
  const [readAt] = useState(new Date().toISOString());
  const sigRef = useRef(null);

  const handleSign = async () => {
    if (!confirmed) { toast.error('Bitte bestätige, dass du die Vereinbarung gelesen hast'); return; }
    const sig = sigRef.current?.getCanvas().toDataURL('image/png');
    if (!sig || sigRef.current?.isEmpty()) { setSigError(true); toast.error('Bitte unterschreiben'); return; }
    setSigning(true);
    try {
      await axios.post(`${API}/api/betriebsvereinbarungen/public/${assignment.token}/sign`, {
        signature: sig,
        confirmed_read: true,
        read_at: readAt,
      });
      toast.success('Betriebsvereinbarung unterschrieben!');
      onSigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler');
    } finally { setSigning(false); }
  };

  return (
    <div className={`bg-white dark:bg-gray-900 border rounded-xl overflow-hidden ${
      assignment.status === 'pending' ? 'border-amber-200 dark:border-amber-700' : 'border-gray-200 dark:border-gray-700'
    }`} data-testid={`bv-card-${assignment.id}`}>
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left"
      >
        <div className="flex-1 min-w-0 pr-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-gray-900 dark:text-white">{assignment.bv_title}</span>
            <StatusBadge status={assignment.status} />
          </div>
          {assignment.employee_signed_at && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Unterschrieben: {new Date(assignment.employee_signed_at).toLocaleDateString('de-DE')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {assignment.status === 'completed' && (
            <button
              onClick={e => { e.stopPropagation(); window.open(`${API}/api/betriebsvereinbarungen/assignments/${assignment.id}/pdf?token=${localStorage.getItem('token')}`, '_blank'); }}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400"
              title="PDF herunterladen"
              data-testid={`download-pdf-${assignment.id}`}
            >
              <Download className="h-4 w-4" />
            </button>
          )}
          {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </button>

      {/* Inhalt */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-100 dark:border-gray-800">
          {/* Text */}
          <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-800/40 rounded-xl space-y-1.5">
            {renderBVContent(assignment.bv_content)}
          </div>

          {/* Bild */}
          {assignment.bv_image && (
            <img src={assignment.bv_image} alt="Vereinbarung" className="rounded-xl border max-h-48 object-contain w-full bg-muted" />
          )}

          {/* Unterschreiben (nur wenn pending) */}
          {assignment.status === 'pending' && (
            <div className="space-y-4">
              <label className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700 rounded-xl cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={e => setConfirmed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300"
                  data-testid="confirm-read-checkbox"
                />
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  {assignment.confirmation_text || 'Ich habe die Betriebsvereinbarung gelesen und verstanden.'}
                </p>
              </label>

              <div className={`rounded-xl overflow-hidden border-2 ${sigError ? 'border-red-400' : 'border-gray-200 dark:border-gray-700'}`}>
                <SignaturePad ref={sigRef} label="Hier unterschreiben" onBegin={() => setSigError(false)} />
              </div>
              {sigError && <p className="text-xs text-red-500">Bitte unterschreiben</p>}

              <Button onClick={handleSign} disabled={signing} className="w-full h-12" data-testid="sign-bv-btn">
                {signing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PenLine className="h-4 w-4 mr-2" />}
                Bestätigen & Unterschreiben
              </Button>
            </div>
          )}

          {/* Bereits unterschrieben */}
          {assignment.status !== 'pending' && (
            <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/10 rounded-xl">
              <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-700 dark:text-green-400">
                Du hast diese Vereinbarung am {new Date(assignment.employee_signed_at).toLocaleDateString('de-DE')} unterschrieben.
                {assignment.status === 'completed' && ' PDF steht zum Download bereit.'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── BV-Text Renderer: **fett** wird wirklich fett, Zeilenumbrüche korrekt ───
function renderBVContent(text) {
  if (!text) return <span className="text-muted-foreground italic">Inhalt wird geladen…</span>;
  return <MarkdownContent text={text} />;
}

export default function MeineBetriebsvereinbarungenPage() {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/betriebsvereinbarungen/assignments/my`, { headers: authHeader() });
      setAssignments(res.data || []);
    } catch (e) {
      toast.error('Fehler beim Laden');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const pending = assignments.filter(a => a.status === 'pending');
  const done = assignments.filter(a => a.status !== 'pending');

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 max-w-2xl mx-auto pb-10 space-y-5">
        <div>
          <h1 className="text-2xl font-bold">Betriebsvereinbarungen</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Deine Vereinbarungen mit dem Arbeitgeber</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : assignments.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="font-semibold">Keine Vereinbarungen</p>
            <p className="text-sm mt-1">Du hast noch keine Betriebsvereinbarungen erhalten.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {pending.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <h2 className="text-sm font-bold text-amber-700 dark:text-amber-400">Ausstehend ({pending.length})</h2>
                </div>
                {pending.map(a => <BVSignCard key={a.id} assignment={a} onSigned={load} />)}
              </div>
            )}
            {done.length > 0 && (
              <div className="space-y-3">
                <h2 className="text-sm font-bold text-gray-500">Unterschrieben ({done.length})</h2>
                {done.map(a => <BVSignCard key={a.id} assignment={a} onSigned={load} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
